import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from 'utils/analytics';
import ROUTE_PATHS from 'utils/routePaths';

/**
 * Maps route path patterns to human-readable screen names.
 * Dynamic segments (e.g. :slotId) are matched by prefix.
 */
const SCREEN_NAMES: Record<string, string> = {
  [ROUTE_PATHS.HOME]: 'Welcome',
  [ROUTE_PATHS.WSM_SIGNIN]: 'Sign In',
  [ROUTE_PATHS.WSM_INITIAL_LOAD]: 'Loading',
  [ROUTE_PATHS.WSM_PROFILE_SETUP]: 'Profile Setup',
  [ROUTE_PATHS.WSM_FAQ]: 'FAQ',
  [ROUTE_PATHS.WSM_KEYS]: 'Keys',
  [ROUTE_PATHS.WSM_HOME]: 'Home',
  [ROUTE_PATHS.WSM_CALENDAR]: 'Calendar',
  [ROUTE_PATHS.WSM_MEETING_ROOM_LIST]: 'Meeting Rooms',
  [ROUTE_PATHS.WSM_CALENDAR_PAYMENT]: 'Checkout',
  [ROUTE_PATHS.WSM_CALENDAR_DF_WORKSPACE_DETAIL]: 'DF Workspace Detail',
  [ROUTE_PATHS.WSM_CALENDAR_INNOVATIVE_SPACE]: 'Innovative Space Detail',
  [ROUTE_PATHS.WSM_CALENDAR_CREATIVE_SPACE]: 'Creative Space Detail',
  [ROUTE_PATHS.WSM_CALENDAR_EVENT_DETAIL]: 'Event Detail',
  [ROUTE_PATHS.WSM_CALENDAR_MB_STUDIO_DETAIL]: 'MB Studio Detail',
  [ROUTE_PATHS.WSM_CALENDAR_COMMUNICATE_SPACE_DETAIL]: 'Communicate Space Detail',
  [ROUTE_PATHS.WSM_MEMBERSHIP_PLAN_DETAIL]: 'Membership Plan',
  [ROUTE_PATHS.WSM_UR_MEMBERSHIP_PLAN_DETAIL]: 'UR Membership Plan',
  [ROUTE_PATHS.WSM_MEMBERSHIP_PAYMENT_DETAIL]: 'Membership Payment',
  [ROUTE_PATHS.WSM_MEMBERSHIP_CREDIT_PLAN_DETAIL]: 'Credit Plan',
  [ROUTE_PATHS.WSM_MEMBERSHIP_ONE_DAY_PASS_DETAIL]: 'One Day Pass',
  [ROUTE_PATHS.WSM_RESERVATION_CANCEL]: 'Reservation Cancelled',
  [ROUTE_PATHS.WSM_PAYMENT_HISTORY]: 'Payment History',
  [ROUTE_PATHS.WSM_PAYMENT_HISTORY_DETAIL]: 'Payment History Detail',
  [ROUTE_PATHS.WSM_ALL_SET]: 'All Set',
  [ROUTE_PATHS.WSM_WEB_LINK_DETAIL]: 'Web Link',
};

/**
 * Resolve a pathname to a screen name.
 * Handles dynamic segments like /calendar/workspace-detail/123
 * by stripping the last segment and checking the prefix.
 */
function resolveScreenName(pathname: string): string {
  // Exact match first
  if (SCREEN_NAMES[pathname]) return SCREEN_NAMES[pathname];

  // Dynamic route: strip last segment and try again (e.g. /calendar/workspace-detail/123)
  const withoutLastSegment = pathname.replace(/\/[^/]+$/, '');
  const dynamicKey = Object.keys(SCREEN_NAMES).find((key) =>
    key.replace(/\/:[^/]+$/, '') === withoutLastSegment,
  );
  if (dynamicKey) return SCREEN_NAMES[dynamicKey];

  // Fallback: use the last path segment, capitalised
  const lastSegment = pathname.split('/').filter(Boolean).pop() ?? pathname;
  return lastSegment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const usePageTracking = (): void => {
  const location = useLocation();

  useEffect(() => {
    const screenName = resolveScreenName(location.pathname);
    // Keep document.title in sync so GA4 also captures a meaningful title
    document.title = `SABE - ${screenName}`;
    trackPageView(location.pathname, screenName);
  }, [location.pathname]);
};

export default usePageTracking;
